import { useBetFormatters } from '@/composables/useBetFormatters';
import { BetOrderCloseReason, BetOrderStatus, BetSelection, BookmakerCode, EventPeriod, Jollywhere } from '@/constants';
import { formatOdds } from '@/helpers/common';
import { AbstractModel } from '@/models/abstractModel';
import { betOrderCloseReasonDictionary, betOrderStatusDictionary, betSelectionDictionary } from '@/stores';
import { IOrderPageApi } from '@/types/order';

export class OrderItem extends AbstractModel {
    public availablePrices: string;
    public betCount: number;
    public betCountWaiting: number;
    public betOdds: string | null;
    public betSelectionId: number;
    public betSiteIds: string | null;
    public betStake: string | null;
    public betTypeId: number;
    public closedAt: string | null;
    public closeReasonId: number | null;
    public corners: string | null;
    public correctScore: string | null;
    public createdAt: string;
    public currencyId: number;
    public customEventId: string;
    public dateGame: string;
    public diffSec: number;
    public handicap: string | null;
    public id: number;
    public internalId: number;
    public isLive: 0 | 1;
    public isTopUps: 0 | 1;
    public jollywhere: number | null;
    public leagueName: string;
    public odds: string;
    public periodTypeId: number;
    public platformId: number;
    public score: string | null;
    public siteIds: string | null;
    public stake: string;
    public statusId: number;
    public sumBetProfit: string | null;
    public sumBetStake: string | null;
    public teamAwayName: string;
    public teamHomeName: string;
    public timeout: number | null;
    public toolUserLogin: string;
    public userId: number;

    constructor(data: IOrderPageApi) {
        super();
        this.id = data.id;
        this.internalId = data.internal_id;
        this.createdAt = data.created_at;
        this.isLive = data.is_live;
        this.statusId = data.status_id;
        this.timeout = data.timeout;
        this.odds = data.odds;
        this.handicap = data.handicap;
        this.periodTypeId = data.period_type_id;
        this.betTypeId = data.bet_type_id;
        this.jollywhere = data.jollywhere;
        this.betSelectionId = data.bet_selection_id;
        this.score = data.score;
        this.corners = data.corners;
        this.isTopUps = data.is_top_ups;
        this.stake = data.stake;
        this.closeReasonId = data.close_reason_id;
        this.closedAt = data.closed_at;
        this.dateGame = data.date_game;
        this.siteIds = data.site_ids;
        this.correctScore = data.correct_score;
        this.availablePrices = data.available_prices;
        this.diffSec = data.diffSec;
        this.teamHomeName = data.team_home_name;
        this.teamAwayName = data.team_away_name;
        this.leagueName = data.league_name;
        this.toolUserLogin = data.tool_user_login;
        this.userId = data.user_id;
        this.platformId = data.tool_user_platform_id;
        this.currencyId = data.currency_id;
        this.betStake = data.bet_stake;
        this.betOdds = data.bet_odds;
        this.sumBetStake = data.sum_bet_stake;
        this.sumBetProfit = data.sum_bet_profit;
        this.betCount = data.bet_count;
        this.betCountWaiting = data.bet_count_waiting;
        this.betSiteIds = data.bet_site_ids;
        this.customEventId = data.custom_event_id;
    }

    get availablePricesList(): Array<{ id: number; code: string }> {
        try {
            const prices = JSON.parse(this.availablePrices);
            const bookmakerIds = Object.keys(prices).map(Number);
            return bookmakerIds.map((id) => ({
                id,
                code: BookmakerCode[id] || `ID:${id}`,
            }));
        } catch {
            return [];
        }
    }

    get betOddsAvg(): string {
        if (!this.sumBetStake || !this.sumBetProfit) return '';
        return formatOdds(Number(this.sumBetProfit) / Number(this.sumBetStake));
    }

    get bookmakerBookmakers(): Array<{ id: number; code: string }> {
        if (!this.betSiteIds) return [];
        const ids = this.betSiteIds
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean);
        return ids.map((id) => {
            const numId = Number(id);
            return {
                id: numId,
                code: BookmakerCode[numId] || `ID:${numId}`,
            };
        });
    }

    get closeReasonClass(): string {
        if (!this.closeReasonId) return '';
        if (this.closeReasonId === BetOrderCloseReason.Normally) {
            return 'text-tbGreen';
        }
        return 'text-tbRed';
    }

    get closeReasonDisplay(): string {
        if (!this.closeReasonId) return '';
        return betOrderCloseReasonDictionary.nameById(this.closeReasonId) || '';
    }

    get executed(): string {
        if (this.betCount === 0) return '-';
        const { formatMoneyCurrency } = useBetFormatters();

        if (!this.sumBetStake) return '-';
        return `${formatMoneyCurrency(this.sumBetStake, this.currencyId)} @ `;
    }

    get executedClass(): string {
        if (this.betCount > 0 && this.betCountWaiting > 0) {
            return 'text-tbYellow';
        }
        return '';
    }

    get executedTooltip(): string | null {
        if (this.takenBetterOdds === null) return null;
        const baseText = this.t('orders.executedTooltipBase');
        return `${baseText} @ ${this.takenBetterOdds.toFixed(3)} ${this.t('orders.executedTooltipSuffix')}`;
    }

    get fillPercent(): string {
        const settledSum = parseFloat(this.sumBetStake || '0');
        const stake = parseFloat(this.stake);
        if (settledSum > 0 && stake > 0) {
            const percent = (settledSum / stake) * 100;
            return `${percent.toFixed(2)}%`;
        }
        return '0.00%';
    }

    get fillPercentClass(): string {
        const settledSum = parseFloat(this.sumBetStake || '0');
        if (settledSum > 0) {
            return this.betCountWaiting > 0 ? 'text-tbYellow' : 'text-tbGreen';
        }
        return 'text-tbRed';
    }

    get marketDisplay(): string {
        const gameType = this.isLive === 1 ? 'LV' : 'PG';
        const period = this.periodTypeId === EventPeriod.FullTime ? 'FT' : '1H';
        return `${gameType} | ${period}`;
    }

    get oddsTooltip(): string {
        try {
            const prices = JSON.parse(this.availablePrices);
            const rows = Object.entries(prices)
                .map(([id, price]) => {
                    const bookmakerId = Number(id);
                    const code = BookmakerCode[bookmakerId] || `ID:${bookmakerId}`;
                    return {
                        code,
                        price: Number(price),
                        id: bookmakerId,
                    };
                })
                .sort((a, b) => b.price - a.price)
                .map(
                    (item) =>
                        `<div style="display: flex; justify-content: space-between; min-width: 150px;"><span>${item.code}</span><span>${item.price}</span></div>`
                )
                .join('');
            return rows;
        } catch {
            return '';
        }
    }

    get orderText(): string {
        let order = '';
        const handicapNum = parseFloat(this.handicap || '0');
        const oddsFormatted = parseFloat(this.odds).toFixed(3);

        switch (this.betSelectionId) {
            case BetSelection.HandicapHome:
            case BetSelection.CornersHandicapHome:
            case BetSelection.SpreadHome:
                order += this.teamHomeName;
                order += ' ';
                if (this.jollywhere !== Jollywhere.Home) {
                    order += handicapNum > 0 ? '+' : '';
                }
                order += this.handicap;
                break;

            case BetSelection.HandicapAway:
            case BetSelection.CornersHandicapAway:
            case BetSelection.SpreadAway:
                order += this.teamAwayName;
                order += ' ';
                if (this.jollywhere !== Jollywhere.Away) {
                    order += handicapNum > 0 ? '+' : '';
                }
                order += this.handicap;
                break;

            case BetSelection.GoalOver:
            case BetSelection.CornersGoalOver:
                order += this.teamHomeName;
                order += ' ' + this.t('orders.gameOver') + ' ';
                order += this.handicap;
                break;

            case BetSelection.GoalUnder:
            case BetSelection.CornersGoalUnder:
                order += this.teamHomeName;
                order += ' ' + this.t('orders.gameUnder') + ' ';
                order += this.handicap;
                break;

            case BetSelection.GoalHomeOver:
                order += this.teamHomeName;
                order += ' ' + this.t('orders.gameHomeOver') + ' ';
                order += this.handicap;
                break;

            case BetSelection.GoalHomeUnder:
                order += this.teamHomeName;
                order += ' ' + this.t('orders.gameHomeUnder') + ' ';
                order += this.handicap;
                break;

            case BetSelection.GoalAwayOver:
                order += this.teamAwayName;
                order += ' ' + this.t('orders.gameAwayOver') + ' ';
                order += this.handicap;
                break;

            case BetSelection.GoalAwayUnder:
                order += this.teamAwayName;
                order += ' ' + this.t('orders.gameAwayUnder') + ' ';
                order += this.handicap;
                break;

            case BetSelection.OneXTwoHome:
                order += this.teamHomeName;
                break;

            case BetSelection.OneXTwoDraw:
                order += this.teamHomeName;
                order += ' ' + this.t('common.draw');
                break;

            case BetSelection.OneXTwoAway:
                order += this.teamAwayName;
                break;

            case BetSelection.DrawNoBetHome:
            case BetSelection.MoneylineHome:
                order += this.teamHomeName;
                break;

            case BetSelection.DrawNoBetAway:
            case BetSelection.MoneylineAway:
                order += this.teamAwayName;
                break;

            case BetSelection.BtsYes:
                order += this.teamHomeName;
                order += ' ' + this.t('orders.gameBtsYes');
                break;

            case BetSelection.BtsNo:
                order += this.teamHomeName;
                order += ' ' + this.t('orders.gameBtsNo');
                break;

            case BetSelection.Dc1X:
                order += this.teamHomeName;
                order += ' ' + this.t('orders.gameHomeOrDraw');
                break;

            case BetSelection.Dc12:
                order += this.teamHomeName;
                order += ' ' + this.t('orders.gameHomeOrAway');
                break;

            case BetSelection.DcX2:
                order += this.teamHomeName;
                order += ' ' + this.t('orders.gameDrawOrAway');
                break;

            case BetSelection.Cs:
                order += this.teamHomeName;
                order += ' ' + this.t('orders.correctScore') + ' ';
                order += this.correctScore || '';
                break;

            default:
                break;
        }

        order += ' ';
        order += '@' + oddsFormatted;

        let score: string;
        if (
            this.betSelectionId === BetSelection.CornersHandicapHome ||
            this.betSelectionId === BetSelection.CornersHandicapAway ||
            this.betSelectionId === BetSelection.CornersGoalOver ||
            this.betSelectionId === BetSelection.CornersGoalUnder
        ) {
            score = this.corners || 'N/C';
        } else {
            score = this.score || 'N/C';
        }

        if (this.isLive === 1) {
            order += ' (' + score + ')';
        }

        return order;
    }

    get orderTooltip(): string {
        const gameType = this.isLive === 1 ? 'Live' : 'Game';
        const periodName = this.periodTypeId === EventPeriod.FullTime ? 'FT' : '1H';
        const selectionName = betSelectionDictionary.getName(this.betSelectionId);
        const score = this.score || '';
        const { formatMoneyCurrency } = useBetFormatters();
        const stake = formatMoneyCurrency(this.stake, this.currencyId);
        const oddsFormatted = parseFloat(this.odds).toFixed(3);

        return `${this.leagueName}<br>${this.teamHomeName} vs ${this.teamAwayName}<br>${gameType} | ${periodName} | ${selectionName}<br>${selectionName} @${oddsFormatted}${score ? ` (${score})` : ''} ${stake}`;
    }

    get outcome(): string {
        const isOpenOrClosing = this.statusId === BetOrderStatus.Open || this.statusId === BetOrderStatus.Closing;
        const isActive = this.diffSec > 0 || this.timeout === null;

        if (isOpenOrClosing && isActive) {
            if (this.betCount === 0) {
                return this.t('orders.attempting');
            } else if (this.betCountWaiting > 0 && this.betCountWaiting === this.betCount) {
                return this.betCountWaiting === 1 ? this.t('orders.oneBetDone') : this.t('orders.betsDone', { count: this.betCountWaiting });
            } else if (this.betCount > 0 && this.isTopUps === 1) {
                return this.t('orders.attemptingTopUp');
            }
        } else {
            if (this.betCount === 0) {
                return this.t('orders.failed');
            } else {
                return this.betCount === 1 ? this.t('orders.oneBetDone') : this.t('orders.betsDone', { count: this.betCount });
            }
        }

        return '';
    }

    get outcomeClass(): string {
        const isOpenOrClosing = this.statusId === BetOrderStatus.Open || this.statusId === BetOrderStatus.Closing;
        const isActive = this.diffSec > 0 || this.timeout === null;

        if (isOpenOrClosing && isActive) {
            if (this.betCount === 0) {
                return 'text-tbYellow';
            } else if (this.betCountWaiting > 0) {
                return 'text-tbYellow';
            } else if (this.betCount > 0 && this.isTopUps === 1) {
                return 'text-tbYellow';
            }
        } else {
            if (this.betCount === 0) {
                return 'text-tbRed';
            } else {
                return 'text-tbGreen';
            }
        }

        return '';
    }

    get selectedBookmakers(): Array<{ id: number; code: string }> | string {
        if (!this.siteIds) return this.t('orders.any');
        const ids = this.siteIds
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean);
        return ids.map((id) => {
            const numId = Number(id);
            return {
                id: numId,
                code: BookmakerCode[numId] || `ID:${numId}`,
            };
        });
    }

    get statusClass(): string {
        if ((this.diffSec <= 0 && this.timeout !== null) || this.statusId === BetOrderStatus.Closed) {
            return '';
        }
        return 'text-tbYellow';
    }

    get statusDisplay(): string {
        if ((this.diffSec <= 0 && this.timeout !== null) || this.statusId === BetOrderStatus.Closed) {
            return betOrderStatusDictionary.nameById(BetOrderStatus.Closed) || 'Closed';
        }
        return betOrderStatusDictionary.nameById(this.statusId) || 'Open';
    }

    get takenBetterOdds(): number | null {
        if (this.betCount === 0 || !this.sumBetStake || !this.sumBetProfit) return null;

        const betOddsAvg = Number(this.sumBetProfit) / Number(this.sumBetStake);
        const orderOdds = parseFloat(this.odds);
        const diff = betOddsAvg - orderOdds;

        return diff >= 0.001 ? diff : null;
    }

    get timeoutFormatted(): string {
        if (this.timeout === null) {
            return this.t('orders.kickOff');
        }

        if (this.isLive === 1) {
            return this.timeout >= 60 ? `${Math.floor(this.timeout / 60)}m` : `${this.timeout}s`;
        } else {
            if (this.timeout < 60) {
                return `${this.timeout}s`;
            } else if (this.timeout < 2700) {
                return `${Math.floor(this.timeout / 60)}m`;
            } else {
                return `${Math.floor(this.timeout / (60 * 60))}h`;
            }
        }
    }
}
